"use client";

import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { mutate as swrMutate } from "swr";
import { MousePointer2, MousePointerClick, Hand, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// BuddyOverlay — onboarding buddy for replay tours.
//
// Rendered when chat.tsx has an active `buddyBeat` (SSE `2:[{type:"buddy_beat",
// ...}]` event, see app/api/routes/replay.py::_buddy_beat_payload). Blocks the
// stream until the viewer clicks Continue; the parent POSTs to
// `/replay/buddy-gate/release` and clears the beat.
//
// Design choices worth keeping:
//   - Spotlight via `box-shadow: 0 0 0 9999px rgba(0,0,0,0.6)` on a div sized
//     to the anchor rect. One element, GPU-friendly, no SVG mask juggling.
//   - Anchor resolution is pluggable (AnchorSpec union). Add more kinds by
//     extending `resolveAnchor` — never hard-code selectors in chat.tsx.
//   - If the anchor element doesn't exist (canvas closed, cell not yet in DOM,
//     bad selector) the overlay falls back to a centered modal. The tour
//     never soft-locks on a missing DOM node.
//   - Esc and outside-click both call `onContinue` so there's always an exit.
//
// Kept deliberately dependency-free: no framer-motion, no shadcn Dialog, no
// portal library. This ships fast and is trivial to audit.
// ─────────────────────────────────────────────────────────────────────────────

export type BuddyAnchor =
  | { kind: "cell_index"; value: number }
  | { kind: "cell_id"; value: string }
  | { kind: "css"; value: string }
  // `latest_role` resolves to the LAST element matching
  // [data-role=value] in DOM order. Used to frame the most-recent
  // assistant/user message at the moment the beat fires (e.g. the
  // final wrap-up summary at the end of a turn). Optional `inner`
  // CSS selector is queried INSIDE the resolved latest-role element
  // and returns the LAST matching descendant — used to box just the
  // closing paragraph of an assistant message rather than the whole
  // message.
  // Optional `take_last`: when paired with `inner`, picks the LAST N
  // matching descendants and unions their bounding rects so the
  // spotlight covers a contiguous range (e.g. the last 5 paragraphs
  // of a long assistant message).
  | { kind: "latest_role"; value: string; inner?: string; take_last?: number }
  | null
  | undefined;

export interface BuddyBeat {
  beatId: string;
  trigger: { type: string; index?: number };
  copy: {
    title?: string;
    body?: string;
    cta?: string;
  };
  anchor?: BuddyAnchor;
  actionTarget?: BuddyAnchor;
  bubbleAnchor?: BuddyAnchor;
  bubbleOffset?: { x?: number; y?: number };
  scrollOffsetY?: number;
  // Optional separate anchor for the spotlight (dim cutout) when it
  // should differ from the interaction anchor. Use case: a
  // click_target beat whose `anchor` is the small `>` arrow inside
  // a cell — setting `spotlight` to the whole cell keeps the cell
  // bright while the click target is still the arrow. Defaults to
  // `anchor`.
  spotlight?: BuddyAnchor;
  action?: { kind?: string; demo?: string } | null;
  spotlightRadius?: number;
  clickHintPlacement?: "auto" | "above";
  // Visual modes for non-blocking guidance — both default to false so
  // existing replay beats keep their classic dimmed-spotlight look.
  //
  // `highlightOnly`: keep the ring/glow on the anchor, but DROP the
  //   78% blackout that surrounds it. The rest of the page stays
  //   fully visible, just with a subtle highlight box around the
  //   target. Use this for soft, non-modal beats where dimming the
  //   page is too heavy (e.g. multi-step "look at this, now this"
  //   walkthroughs of the alert page UI where the user needs to see
  //   how everything relates).
  //
  // `noAnchor`: render JUST the bubble centered in the viewport — no
  //   ring, no spotlight, no dim, nothing painted on the page behind
  //   it. Used for purely-narrative beats like the welcome/intro slide
  //   that are about reading copy, not about pointing at UI.
  highlightOnly?: boolean;
  hideSpotlightRing?: boolean;
  noAnchor?: boolean;
  // `noScroll`: when true, suppresses the auto-scroll-into-view that
  // BuddyOverlay normally performs when an anchor is offscreen. Used
  // for tour beats where the user is already meant to be looking at
  // the right part of the page (e.g. the home feed pill click beats
  // that resume after the alert tour returns home — the page was
  // already at the top, no need to nudge it). Without this the rect
  // poll's `scrollIntoView({block: "start"})` can shove the page up
  // a few pixels and make the bubble jitter on first paint.
  noScroll?: boolean;
  // Where to put the bubble when there's no anchor rect to position it
  // against. Defaults to "center" (vertically centered, pinned to the
  // right edge of the viewport). "bottomRight" pins the bubble to the
  // lower-right corner — used for the alert tour's welcome beat where
  // the user has just landed on a fresh page and the centered placement
  // feels too modal / blocking.
  // "anchorBelow" forces the bubble to sit DIRECTLY BELOW the anchor
  // rect (horizontally centered on the rect, with viewport-edge
  // clamping). Used for click_target beats whose anchor sits in the
  // top-right corner (history icon, three-dot menu trigger). The
  // default click_target adjacency would prefer LEFT for those, which
  // clamps the bubble against the top-left edge and visually crowds
  // the highlighted icon. "anchorBelow" keeps the bubble in proximity
  // to the target (right under it) without obscuring it.
  bubblePlacement?: "center" | "bottomRight" | "topCenter" | "anchor" | "anchorAbove" | "anchorBelow" | "anchorTopRight" | "anchorLeft" | "modalSidebar" | "modalRight" | "outsideModalRight";
  waitUntilGone?: BuddyAnchor;
  // Permissive escape hatch for click_target beats whose anchored
  // widget mutates the DOM in a way that breaks the
  // pointerdown-then-click pairing (Radix DropdownMenu trigger,
  // sidebar opener that relayouts the topbar, etc.). When true:
  //   • the off-target click guard NEVER fires while this beat is
  //     active — any click anywhere is treated as "on-target"
  //     instead of bouncing the user into the exit-confirm modal.
  //   • the click_target advance listener releases the beat on the
  //     FIRST click anywhere on the document, not just clicks inside
  //     the anchor. Saves the user from soft-locking when the click
  //     event's target gets re-targeted to <html> by Radix etc.
  // Use sparingly — only when the strict-target guard provably
  // misbehaves on the underlying widget.
  releaseOnAnyClick?: boolean;
}

interface BuddyOverlayProps {
  beat: BuddyBeat | null;
  onContinue: (beatId: string) => void;
  onExit?: () => void;
}

// Tightened from 10→ 4 in the alert-tour redesign. The previous value
// gave a generous halo around each highlighted component, which read
// as "big chunky modal box" rather than a precise pointer at the UI.
// 4px keeps the ring clearly OUTSIDE the component's own border so the
// rounded corners don't visually merge, but no more.
const SPOTLIGHT_PADDING = 4;         // px of breathing room around the anchor rect
const BUBBLE_WIDTH = 320;            // px — bubble is pinned at right edge of viewport
const BUBBLE_RIGHT_OFFSET = 24;      // px — gap between bubble and right viewport edge
// Hard cap on spotlight height as a fallback when the input bar can't
// be located (e.g. workflow / artifact views that don't render one).
// In normal chat-replay use the runtime clamp below — which pins the
// spotlight bottom above the docked MultimodalInput — takes over.
const MAX_SPOTLIGHT_HEIGHT = 700;
// Margin between the spotlight bottom edge and the top of the
// multimodal input bar. Picked so the input bar reads visually as
// "outside" the spotlight even with the soft 78% blackout dimmer.
const SPOTLIGHT_INPUT_BAR_MARGIN = 16;

// Returns true when the anchor lives in a layer that visually floats
// ABOVE the chat-panel flow (alert canvas pane, AlertCellModal portal,
// or any other fixed-positioned overlay). For those anchors the
// docked MultimodalInput bar can't actually obscure the spotlight —
// the input bar paints below the modal and outside the canvas pane —
// so the bar-bottom clamp is wrong and clips the spotlight to a
// shorter height than the anchor's natural rect.
//
// Walks up the DOM rather than guessing from rect coordinates. The
// previous heuristic (`spotlight.left >= bar.right`) failed for the
// AlertCellModal because the modal is centered on the viewport —
// its left edge sits well inside the chat panel's x-range even
// though it portals over everything visually.
function isAnchorAboveChatBar(el: HTMLElement | null): boolean {
  if (!el || typeof window === "undefined") return false;
  let cur: HTMLElement | null = el;
  while (cur && cur !== document.body) {
    const testid = cur.getAttribute?.("data-testid");
    if (
      testid === "alert-cell-modal" ||
      testid === "alert-canvas" ||
      // The alert-mode chat column wraps the docked MultimodalInput
      // bar — its natural rect already includes the bar at the
      // bottom. Applying the bar-top clamp here would chop the input
      // bar OFF the spotlight, leaving a thin dim band beneath the
      // bright column. For ask_user "quoting" beats that target the
      // chat-column we want the WHOLE right strip lit (including
      // the bar) so the viewer sees the full context.
      testid === "alert-chat-column"
    ) {
      return true;
    }
    // `position: fixed` is the universal signal for "rendered outside
    // normal document flow" — modals (Radix/Headless UI/custom),
    // dropdowns, the alert canvas itself (z-50 portal). All of those
    // float above the docked input bar regardless of where their
    // bounding rect happens to land in viewport coords.
    const cs = window.getComputedStyle(cur);
    if (cs.position === "fixed") return true;
    cur = cur.parentElement;
  }
  return false;
}

// Compute the maximum spotlight height for a given top edge by
// looking at the live position of the docked MultimodalInput bar.
// Returns the constant fallback when no input bar is on screen.
//
// `anchorEl` is the first element of the anchor's resolved set;
// passing it lets us short-circuit the bar clamp for anchors that
// float above the chat flow (canvas, modal). Without this short-
// circuit the bar-bottom clamp AND the 700px hard cap chop the
// spotlight on tall canvas/modal anchors.
function maxSpotlightHeight(
  top: number,
  anchorEl: HTMLElement | null = null,
): number {
  if (typeof document === "undefined") return MAX_SPOTLIGHT_HEIGHT;
  // Anchors floating above the chat flow — use viewport-bounded
  // height with NO 700px cap. The cap was tuned for chat cells where
  // a too-tall spotlight would chase past the screen; canvas / modal
  // anchors are themselves bounded by the viewport (they're fixed-
  // positioned), so the natural rect height is already safe.
  if (isAnchorAboveChatBar(anchorEl)) {
    if (typeof window === "undefined") return MAX_SPOTLIGHT_HEIGHT;
    return Math.max(0, (window.innerHeight || 800) - top - 16);
  }
  const bar = document.querySelector<HTMLElement>(
    '[data-testid="multimodal-input-bar"]'
  );
  if (!bar) return MAX_SPOTLIGHT_HEIGHT;
  const barTop = bar.getBoundingClientRect().top;
  const limit = barTop - SPOTLIGHT_INPUT_BAR_MARGIN - top;
  if (!isFinite(limit) || limit <= 0) return MAX_SPOTLIGHT_HEIGHT;
  return Math.min(MAX_SPOTLIGHT_HEIGHT, limit);
}

function resolveAnchor(anchor: BuddyAnchor): HTMLElement | null {
  if (!anchor) return null;
  try {
    if (anchor.kind === "cell_index") {
      return document.querySelector<HTMLElement>(
        `[data-cell-index="${CSS.escape(String(anchor.value))}"]`
      );
    }
    if (anchor.kind === "cell_id") {
      return document.querySelector<HTMLElement>(
        `[data-cell-id="${CSS.escape(anchor.value)}"]`
      );
    }
    if (anchor.kind === "css") {
      return document.querySelector<HTMLElement>(anchor.value);
    }
    if (anchor.kind === "latest_role") {
      const els = document.querySelectorAll<HTMLElement>(
        `[data-role="${CSS.escape(anchor.value)}"]`
      );
      const root = els.length ? els[els.length - 1] : null;
      if (!root) return null;
      if (!anchor.inner) return root;
      const inner = root.querySelectorAll<HTMLElement>(anchor.inner);
      if (!inner.length) return root;
      // For single-element resolution we always return the LAST
      // match. The multi-element variant (resolveAnchorElements) is
      // what honours take_last for the rect union.
      return inner[inner.length - 1];
    }
  } catch {
    // Malformed selector — fall through to null so the overlay centers.
  }
  return null;
}

function resolveActionableTarget(el: HTMLElement): HTMLElement {
  const selector =
    "button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[role='button'],[tabindex]:not([tabindex='-1'])";
  if (el.matches(selector)) return el;
  return el.querySelector<HTMLElement>(selector) ?? el;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

// Resolve an anchor to one OR MORE elements. Used by the spotlight
// rect computation so an anchor like `latest_role + inner +
// take_last: 5` can frame a contiguous range (last 5 paragraphs)
// rather than a single element. All other anchor kinds collapse to
// a 1-element array.
function resolveAnchorElements(anchor: BuddyAnchor): HTMLElement[] {
  if (!anchor) return [];
  if (anchor.kind === "latest_role") {
    try {
      const roots = document.querySelectorAll<HTMLElement>(
        `[data-role="${CSS.escape(anchor.value)}"]`
      );
      const root = roots.length ? roots[roots.length - 1] : null;
      if (!root) return [];
      if (!anchor.inner) return [root];
      const inner = Array.from(
        root.querySelectorAll<HTMLElement>(anchor.inner)
      );
      if (!inner.length) return [root];
      const n = Math.max(1, anchor.take_last ?? 1);
      return inner.slice(Math.max(0, inner.length - n));
    } catch {
      return [];
    }
  }
  const single = resolveAnchor(anchor);
  return single ? [single] : [];
}

// Union the bounding rects of a non-empty element list into a single
// rect. Returns null on empty input. Used as the source-of-truth for
// the spotlight cutout when an anchor resolves to multiple elements.
function unionRects(els: HTMLElement[]): DOMRect | null {
  if (!els.length) return null;
  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.top < top) top = r.top;
    if (r.left < left) left = r.left;
    if (r.right > right) right = r.right;
    if (r.bottom > bottom) bottom = r.bottom;
  }
  return new DOMRect(left, top, right - left, bottom - top);
}

function scrollByViewportDelta(anchorEl: HTMLElement, delta: number, behavior: ScrollBehavior) {
  if (Math.abs(delta) < 4) return;
  let cur = anchorEl.parentElement;
  while (cur && cur !== document.body) {
    const style = window.getComputedStyle(cur);
    const canScroll =
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      cur.scrollHeight > cur.clientHeight + 1;
    if (canScroll) {
      cur.scrollBy({ top: delta, behavior });
      return;
    }
    cur = cur.parentElement;
  }
  window.scrollBy({ top: delta, behavior });
}

function useAnchorRect(
  anchor: BuddyAnchor,
  active: boolean,
  scrollAnchorIntoView: boolean = true,
  scrollOffsetY?: number,
): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useLayoutEffect(() => {
    setRect(null);
    if (!active) {
      return;
    }
    // Pause the continuous auto-scroll the INSTANT the beat becomes
    // active — before we even start polling for the anchor. Doing it
    // later (e.g. right before scrollIntoView) leaves a window of up
    // to ~100ms where the MutationObserver in use-scroll-to-bottom
    // sees `isAtBottom=true`, snaps to the bottom on each stream
    // chunk, and the user sees a yo-yo before the buddy frames the
    // cell at the top. handleContinue restores the flag on release.
    swrMutate("messages:is-at-bottom", false, false);
    // Poll for the anchor element. Cells render asynchronously after SSE
    // delivery (especially heavy outputs), so the anchor may not exist at
    // the moment we receive the beat. Give it up to ~3s; after that we
    // render centered as a graceful fallback.
    let el: HTMLElement | null = null;
    let ticks = 0;
    const MAX_TICKS = 20;
    const INTERVAL_MS = 100;
    let cancelled = false;
    let settleTimer: number | null = null;

    let els: HTMLElement[] = [];
    const read = () => {
      if (cancelled) return;
      const r = unionRects(els);
      if (!r) return;
      const top = r.top - SPOTLIGHT_PADDING;
      const left = r.left - SPOTLIGHT_PADDING;
      setRect({
        top,
        left,
        width: r.width + SPOTLIGHT_PADDING * 2,
        // Pass the first element so maxSpotlightHeight can DOM-walk
        // to detect fixed-position ancestors (modal portals, canvas
        // pane). Those anchors skip the bar-bottom clamp + 700px
        // hard cap so the spotlight can frame their full extent.
        height: Math.min(
          r.height + SPOTLIGHT_PADDING * 2,
          maxSpotlightHeight(top, els[0] ?? null),
        ),
      });
    };

    const poll = window.setInterval(() => {
      if (cancelled) return;
      els = resolveAnchorElements(anchor);
      ticks += 1;
      if (els.length) {
        // Only scroll if the FIRST element of the union isn't already
        // framed near the top of the viewport. Without this, every
        // consecutive beat on the same cell re-fires scrollIntoView,
        // which the user perceives as a yo-yo whenever a previous
        // beat just changed the cell's height (e.g. flipping carousel
        // tabs with differently sized plotly figures).
        const first = els[0];
        const r = first.getBoundingClientRect();
        const vh = window.innerHeight || 800;
        const targetTop =
          scrollOffsetY == null
            ? null
            : Math.max(16, Math.min(vh * 0.68, scrollOffsetY));
        const horizontallyFramed =
          r.left >= 8 && r.right <= (window.innerWidth || 1200) - 8;
        const verticallyFramed =
          scrollOffsetY == null
            ? r.top >= -16 && r.top <= vh * 0.25
            : targetTop != null && Math.abs(r.top - targetTop) <= 32;
        const alreadyFramed = verticallyFramed && horizontallyFramed;
        if (scrollAnchorIntoView && !alreadyFramed) {
          first.scrollIntoView({
            behavior: "smooth",
            block: scrollOffsetY == null ? "start" : "center",
            inline: "center",
          });
          if (scrollOffsetY != null) {
            window.setTimeout(() => {
              if (cancelled) return;
              const nextRect = first.getBoundingClientRect();
              const nextTargetTop = Math.max(16, Math.min(vh * 0.68, scrollOffsetY));
              scrollByViewportDelta(first, nextRect.top - nextTargetTop, "smooth");
            }, 120);
          }
          // Let the smooth-scroll settle before the first rect read —
          // otherwise the spotlight snaps to the pre-scroll position
          // and catches up a frame later.
          settleTimer = window.setTimeout(read, scrollOffsetY == null ? 320 : 460);
        } else {
          // No scroll needed — read immediately so the spotlight
          // appears in place rather than after a 320ms gap.
          read();
        }
        window.clearInterval(poll);
      } else if (ticks >= MAX_TICKS) {
        window.clearInterval(poll);
      }
    }, INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      if (settleTimer != null) window.clearTimeout(settleTimer);
    };
  }, [anchor, active, scrollAnchorIntoView, scrollOffsetY]);

  // re-fires scroll, which re-fires setRect — a feedback loop that
  // manifests as visible spotlight jitter / shaking after a few cells.
  // rAF-throttle so we update at most once per frame, and use a ref
  // for the latest rect to skip no-op setStates that would still
  // trigger re-renders.
  useEffect(() => {
    if (!active) return;
    let rafId: number | null = null;
    let lastSerialized = "";
    const update = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const els = resolveAnchorElements(anchor);
        const r = unionRects(els);
        if (!r) return;
        const top = r.top - SPOTLIGHT_PADDING;
        const left = r.left - SPOTLIGHT_PADDING;
        const next = {
          top,
          left,
          width: r.width + SPOTLIGHT_PADDING * 2,
          height: Math.min(
            r.height + SPOTLIGHT_PADDING * 2,
            maxSpotlightHeight(top, els[0] ?? null),
          ),
        };
        // Skip identical-rect updates (sub-pixel drift causes setState
        // spam during long scrolls / resize observers ticking).
        const serialized = `${Math.round(next.top)},${Math.round(next.left)},${Math.round(next.width)},${Math.round(next.height)}`;
        if (serialized === lastSerialized) return;
        lastSerialized = serialized;
        setRect(next);
      });
    };
    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", update, { capture: true } as any);
      window.removeEventListener("resize", update);
    };
  }, [anchor, active]);

  return rect;
}

function useAnchorFallbackReady(
  anchor: BuddyAnchor,
  active: boolean,
  rect: Rect | null,
): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    if (!active || !anchor || rect) return;
    const timer = window.setTimeout(() => {
      setReady(true);
    }, 3200);
    return () => window.clearTimeout(timer);
  }, [anchor, active, rect]);

  return ready;
}

// Bubble is pinned at a fixed location on the right edge of the viewport
// (vertically centered). It used to chase the anchor rect — that placed
// the bubble *on top of subsequent cells* when the highlighted cell was
// short, and *below the fold* when the highlighted cell sat at the
// bottom of the messages stream. A fixed pin guarantees the bubble is
// always fully visible without colliding with the spotlighted content.
// See `bubbleStyle` below for the actual placement.

export function BuddyOverlay({ beat, onContinue, onExit }: BuddyOverlayProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const anchor = beat?.anchor ?? null;
  const actionAnchor = beat?.actionTarget ?? beat?.anchor ?? null;
  // Spotlight anchor defaults to the interaction anchor. Pass it
  // separately so click_target beats can keep the cell bright while
  // pointing at a small inner button.
  const spotlightAnchor = beat?.spotlight ?? beat?.anchor ?? null;
  const active = Boolean(beat);
  const beatId = beat?.beatId ?? null;
  const isMobileViewport =
    typeof window !== "undefined" && window.innerWidth <= 767;
  const isMobileCellBeat =
    isMobileViewport && spotlightAnchor?.kind === "cell_index";
  const shouldLiftFinalSummary =
    !isMobileViewport && beatId === "final_summary";
  // anchorAbove beats need the section scrolled DOWN far enough to
  // leave room for the bubble above it. The blog-article tour was
  // passing `scrollOffsetY: 72` — that lands rect.top at ~72px,
  // leaving only ~46px usable above after gap + margin. The
  // anchorAbove geometry pins the bubble's BOTTOM edge `GAP` above
  // rect.top, so any bubble content taller than ~46px overflows the
  // top of the viewport (desktop AND mobile). User feedback after
  // the previous mobile-only fix: "you fucked it up for desktop now
  // man but it works for mobile". Floor scrollOffsetY universally
  // so the bubble always fits above. Per-beat values larger than
  // the floor are respected (some beats explicitly push the section
  // further down).
  // 300 → maxHeight ≈ rect.top - GAP - MARGIN ≈ 274 in the
  // anchorAbove geometry, which comfortably fits a typical bubble
  // (title + 3-4 line body + CTA + exit footer ≈ 230-250px). 200
  // was *too tight*: maxHeight came out to ~174 and the trailing
  // CTA/exit row painted BELOW the bubble's visual box (no
  // overflow rule). User feedback: "now the buttons don't show at
  // all... this was occurring because of the scroll movements".
  const ANCHOR_ABOVE_MIN_OFFSET = 300;
  const isAnchorAboveBeat = beat?.bubblePlacement === "anchorAbove";
  const beatScrollOffsetY = isAnchorAboveBeat
    ? Math.max(beat?.scrollOffsetY ?? 0, ANCHOR_ABOVE_MIN_OFFSET)
    : beat?.scrollOffsetY;
  const effectiveScrollOffsetY =
    beatScrollOffsetY ??
    (shouldLiftFinalSummary && typeof window !== "undefined"
      ? Math.min(220, Math.max(140, window.innerHeight * 0.22))
      : undefined) ??
    // Mobile cell beats: pull the cell HIGHER on the screen than the
    // pre-2026-05 default (which framed cells at 260-320px from the
    // top). The old value left the bubble visually centered with the
    // cell — and on tall cells that pushed the bottom of the cell off
    // the viewport entirely. New values land the cell at ~180-220px
    // from the top, leaving room for the (now-compact) mobile bubble
    // above while keeping more cell content in the visible region
    // below. User feedback: "cells underneath are forced lower and
    // thus are not even visible fully".
    (isMobileCellBeat && typeof window !== "undefined"
      ? Math.min(220, Math.max(180, window.innerHeight * 0.26))
      : undefined);
  // Per-beat opt-out for the auto-scroll. Most replay beats want the
  // anchor framed near the top of the viewport, but cross-page tour
  // beats that resume after a navigation are usually already in the
  // right scroll position and the extra nudge just causes a flicker.
  const scrollAnchorIntoView = !beat?.noScroll;
  const rect = useAnchorRect(spotlightAnchor, active, scrollAnchorIntoView, effectiveScrollOffsetY);
  const anchorFallbackReady = useAnchorFallbackReady(
    spotlightAnchor,
    active && Boolean(spotlightAnchor) && !beat?.noAnchor,
    rect,
  );
  const bubbleAnchor = beat?.bubbleAnchor ?? beat?.anchor ?? spotlightAnchor;
  const hasSeparateBubbleAnchor =
    Boolean(bubbleAnchor) && bubbleAnchor !== spotlightAnchor;
  const bubbleAnchorRect = useAnchorRect(
    hasSeparateBubbleAnchor ? bubbleAnchor : null,
    active && hasSeparateBubbleAnchor,
    false,
  );
  const bubbleRect = hasSeparateBubbleAnchor ? bubbleAnchorRect : rect;
  // Separate rect for the click_target indicator (green pulsing
  // ring + "Click" pill). Tracks the interaction anchor (e.g. the
  // small `>` arrow) even when `spotlight` is set to the whole cell
  // — so the dim cutout frames the cell but the click indicator
  // stays tight on the actual button. Computed only when the two
  // anchors differ; otherwise falls back to the spotlight rect to
  // avoid duplicate work for the common case (anchor === spotlight).
  const hasSeparateClickAnchor =
    Boolean(actionAnchor) && actionAnchor !== spotlightAnchor;
  const clickAnchorRect = useAnchorRect(
    hasSeparateClickAnchor ? actionAnchor : null,
    active && hasSeparateClickAnchor,
    false,
  );
  const clickRect = hasSeparateClickAnchor ? clickAnchorRect : rect;
  // `click_target` beats only release when the viewer clicks the
  // anchored element itself (e.g. a carousel's "Next" button). The
  // Continue button is hidden, the dimmer's click-to-skip is disabled,
  // and the bubble is repositioned next to the spotlight so the
  // connection between prompt and target is obvious.
  const isClickTarget = beat?.action?.kind === "click_target";
  const isNextClickTarget = beat?.action?.kind === "click_on_next";
  // See the BuddyBeat.releaseOnAnyClick comment. Permissive mode for
  // click_target beats wrapping widgets that mutate the DOM mid-click
  // (Radix DropdownMenu trigger, sidebar opener relayouting the
  // topbar). Disables the off-target guard and advances on any click.
  const releaseOnAnyClick = beat?.releaseOnAnyClick === true;
  const [dismissedClickChromeBeatId, setDismissedClickChromeBeatId] = useState<string | null>(null);
  // Inline gesture demo. Currently supports "drag_zoom" — renders an
  // animated cursor that mimes a click-and-drag selection over the
  // anchor rect, hint pill labelling the gesture. Used to teach the
  // user that the spotlighted chart is interactive (Plotly's box-zoom
  // gesture). Layered above the spotlight ring, pointer-events-none.
  const demo = beat?.action?.demo ?? null;
  const isDragZoomDemo = demo === "drag_zoom";
  const isDoubleClickResetDemo = demo === "double_click_reset";
  const gestureAnchor = actionAnchor ?? anchor;
  // Canvas-side gesture demos. `canvas_pan` teaches click-and-drag
  // panning on the alert canvas (React Flow); same drag detector as
  // drag_zoom but the rendered animation is different (no selection
  // rect, just a horizontal sweep). `canvas_pinch_zoom` teaches
  // pinch-to-zoom (trackpad) / Ctrl-scroll (mouse) and is detected
  // via wheel events with ctrlKey — the standard convention browsers
  // use for both gestures.
  const isCanvasPanDemo = demo === "canvas_pan";
  const isCanvasPinchZoomDemo = demo === "canvas_pinch_zoom";
  // `modal_scroll` is a purely-decorative demo: an animated chevron
  // bouncing downward inside the spotlight, paired with a "Scroll" hint
  // pill. No gesture detection — the user advances via the Next button
  // when they're done reading. Used by the alert-tour dashboard-explore
  // beat where the modal content is taller than the viewport.
  const isModalScrollDemo = demo === "modal_scroll";
  const spotlightBorderRadius = beat?.spotlightRadius ?? 12;
  // Spotlight click-to-skip is suppressed whenever an interactive
  // affordance is active — click_target needs the user to actually
  // click the anchored element underneath, and gesture demos need
  // the user to be able to try the gesture on the underlying surface.
  // Without this, the underlying handlers (Plotly box-zoom, React
  // Flow pan/zoom) get swallowed by the spotlight div's
  // onClick={handleContinue} and the beat dismisses prematurely.
  const allowSpotlightClickToSkip = !isClickTarget && demo == null;

  // Pending resume timer. We DEFER the scroll-to-bottom + isAtBottom
  // restoration so consecutive beats on the same cell (e.g. drag_zoom
  // → double_click_reset) don't yo-yo the page: bottom-snap on
  // continue, then back to cell-top on next beat mount. That round-
  // trip also forced Plotly to relayout, wiping the user's box-zoom
  // even with uirevision set. By deferring, the next beat's onMount
  // (which fires within ~1 render) cancels the pending resume, so
  // we stay parked on the cell.
  const pendingResumeRef = useRef<number | null>(null);
  const continuedBeatRef = useRef<string | null>(null);
  const [showExitGuard, setShowExitGuard] = useState(false);
  const hideClickTargetVisuals = useCallback((beatId: string) => {
    setDismissedClickChromeBeatId(beatId);
  }, []);
  const handleContinue = useCallback(() => {
    if (!beat) return;
    if (continuedBeatRef.current === beat.beatId) return;
    continuedBeatRef.current = beat.beatId;
    // Dismiss the overlay chrome (spotlight ring + bubble) for the
    // current beat the instant Continue is invoked — independent of
    // beat kind. Previously this was scoped to click_target so the
    // green ring/pill would vanish on action; for normal Next clicks
    // (and Esc / dimmer click-to-skip) we left the spotlight ring
    // mounted, and because the rect updater keeps tracking the anchor
    // as it animates/scrolls, the ring would visibly "glide off"
    // alongside a closing modal or scrolling cell before the next
    // beat finally took over. Generalising the dismissal kills that
    // tracking immediately so the old ring disappears in the same
    // frame the user advances.
    hideClickTargetVisuals(beat.beatId);
    const shouldResumeAfterContinue = !(
      beat.action?.kind === "click_target" &&
      beat.anchor &&
      beat.spotlight
    );
    onContinue(beat.beatId);
    if (pendingResumeRef.current != null) {
      window.clearTimeout(pendingResumeRef.current);
    }
    if (!shouldResumeAfterContinue) {
      pendingResumeRef.current = null;
      return;
    }
    pendingResumeRef.current = window.setTimeout(() => {
      pendingResumeRef.current = null;
      swrMutate("messages:is-at-bottom", true, false);
      if (typeof document !== "undefined") {
        const messagesEl = document.querySelector<HTMLElement>(
          "[data-chat-messages]"
        );
        if (messagesEl) {
          messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: "instant" });
        }
      }
    }, 280);
  }, [beat, hideClickTargetVisuals, onContinue]);

  // Cancel any pending resume the moment a new beat arrives — the
  // beat's own onMount logic handles positioning (cell scrolled to
  // top + isAtBottom=false), and we don't want a stale bottom-snap
  // from the previous handleContinue to fire on top of it.
  useEffect(() => {
    if (!beatId) return;
    continuedBeatRef.current = null;
    setDismissedClickChromeBeatId(null);
    setShowExitGuard(false);
    if (pendingResumeRef.current != null) {
      window.clearTimeout(pendingResumeRef.current);
      pendingResumeRef.current = null;
    }
  }, [beatId]);

  // Cleanup any pending resume on unmount.
  useEffect(() => {
    return () => {
      if (pendingResumeRef.current != null) {
        window.clearTimeout(pendingResumeRef.current);
        pendingResumeRef.current = null;
      }
    };
  }, []);

  const requestExitGuard = useCallback(() => {
    setShowExitGuard(true);
  }, []);

  const handleExit = useCallback(() => {
    requestExitGuard();
  }, [requestExitGuard]);

  const handleConfirmExit = useCallback(() => {
    setShowExitGuard(false);
    onExit?.();
  }, [onExit]);

  const handleStayInTour = useCallback(() => {
    setShowExitGuard(false);
  }, []);

  const waitForGoneAndContinue = useCallback(
    (beatId: string, waitUntilGone?: BuddyAnchor) => {
      if (!waitUntilGone) {
        onContinue(beatId);
        return;
      }
      const startedAt = performance.now();
      const tick = () => {
        const gone = !resolveAnchor(waitUntilGone);
        const timedOut = performance.now() - startedAt > 1200;
        if (gone || timedOut) {
          onContinue(beatId);
          return;
        }
        window.setTimeout(tick, 50);
      };
      window.setTimeout(tick, 50);
    },
    [onContinue],
  );

  const handlePrimaryAction = useCallback(() => {
    if (!beat) return;
    if ((!isClickTarget && !isNextClickTarget) || !actionAnchor) {
      handleContinue();
      return;
    }
    const beatId = beat.beatId;
    const waitUntilGone = beat.waitUntilGone;
    if (isClickTarget) {
      hideClickTargetVisuals(beatId);
    }

    const anchorTarget = resolveAnchor(actionAnchor);
    if (!anchorTarget) {
      handleContinue();
      return;
    }
    const target = resolveActionableTarget(anchorTarget);

    const bounds = target.getBoundingClientRect();
    const clientX = bounds.left + bounds.width / 2;
    const clientY = bounds.top + bounds.height / 2;
    const pointerBase = {
      bubbles: true,
      cancelable: true,
      composed: true,
      pointerId: 1,
      pointerType: "mouse",
      isPrimary: true,
      clientX,
      clientY,
      button: 0,
    };
    const mouseBase = {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
      clientX,
      clientY,
      button: 0,
    };

    window.setTimeout(() => {
      if (waitUntilGone) {
        target.click();
        waitForGoneAndContinue(beatId, waitUntilGone);
        window.setTimeout(() => {
          if (!resolveAnchor(waitUntilGone)) return;
          if (typeof PointerEvent !== "undefined") {
            target.dispatchEvent(new PointerEvent("pointerdown", { ...pointerBase, buttons: 1 }));
          }
          target.dispatchEvent(new MouseEvent("mousedown", { ...mouseBase, buttons: 1 }));
          if (typeof PointerEvent !== "undefined") {
            target.dispatchEvent(new PointerEvent("pointerup", { ...pointerBase, buttons: 0 }));
          }
          target.dispatchEvent(new MouseEvent("mouseup", { ...mouseBase, buttons: 0 }));
          target.dispatchEvent(new MouseEvent("click", mouseBase));
        }, 100);
      } else {
        if (typeof PointerEvent !== "undefined") {
          target.dispatchEvent(new PointerEvent("pointerdown", { ...pointerBase, buttons: 1 }));
        }
        target.dispatchEvent(new MouseEvent("mousedown", { ...mouseBase, buttons: 1 }));
        if (typeof PointerEvent !== "undefined") {
          target.dispatchEvent(new PointerEvent("pointerup", { ...pointerBase, buttons: 0 }));
        }
        target.dispatchEvent(new MouseEvent("mouseup", { ...mouseBase, buttons: 0 }));
        target.click();
      }
      if (!waitUntilGone) {
        window.setTimeout(() => {
          waitForGoneAndContinue(beatId, waitUntilGone);
        }, 300);
      }
    }, 0);
  }, [actionAnchor, beat, handleContinue, hideClickTargetVisuals, isClickTarget, isNextClickTarget, waitForGoneAndContinue]);

  // Esc to skip — always-on escape hatch so the tour never soft-locks.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        requestExitGuard();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, requestExitGuard]);

  const isAllowedTourTarget = useCallback(
    (target: EventTarget | null) => {
      if (!active) return true;
      // Permissive escape hatch — see BuddyBeat.releaseOnAnyClick. For
      // beats wrapping widgets like Radix DropdownMenu trigger or the
      // sidebar opener that mutate the DOM between pointerdown and
      // mouseup, the strict `actionAnchor.contains(target)` check
      // falsely blocks the user's natural click (browser re-targets
      // the click event to <html>). Treat ANY click as on-target.
      if (releaseOnAnyClick) return true;
      if (!(target instanceof Node)) return false;
      const targetElement =
        target instanceof Element ? target : target.parentElement;
      if (
        targetElement?.closest(
          "[data-buddy-bubble='true'],[data-buddy-exit-confirm='true']",
        )
      ) {
        return true;
      }
      const allowedAnchors: BuddyAnchor[] = [];
      if (isClickTarget || isNextClickTarget) {
        allowedAnchors.push(actionAnchor);
      }
      if (
        isDragZoomDemo ||
        isDoubleClickResetDemo ||
        isCanvasPanDemo ||
        isCanvasPinchZoomDemo
      ) {
        allowedAnchors.push(gestureAnchor);
      }
      return allowedAnchors.some((allowedAnchor) => {
        const root = resolveAnchor(allowedAnchor);
        return !!root && root.contains(target);
      });
    },
    [
      active,
      actionAnchor,
      gestureAnchor,
      isCanvasPanDemo,
      isCanvasPinchZoomDemo,
      isClickTarget,
      isDoubleClickResetDemo,
      isDragZoomDemo,
      isNextClickTarget,
      releaseOnAnyClick,
    ],
  );

  useEffect(() => {
    if (!active) return;
    const onAttemptedOffTargetInteraction = (e: MouseEvent | PointerEvent) => {
      // Mobile fix: a touch-pointerdown fires the MOMENT the finger
      // lands on the screen, BEFORE the browser knows whether the
      // gesture is a tap or a scroll. If we run the off-target guard
      // here, every scroll on the page pops the exit-confirm modal —
      // the user can't even read the alert page without being asked
      // if they want to leave the tour. Skip touch pointerdowns; we
      // still catch genuine touch TAPS via the synthetic `mousedown`
      // and `click` events the browser dispatches only after a touch
      // resolves as a tap (NOT a scroll). Mouse/pen pointerdowns are
      // still guarded normally.
      if (
        e.type === "pointerdown" &&
        (e as PointerEvent).pointerType === "touch"
      ) {
        return;
      }
      if (isAllowedTourTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      requestExitGuard();
    };
    document.addEventListener("pointerdown", onAttemptedOffTargetInteraction, true);
    document.addEventListener("mousedown", onAttemptedOffTargetInteraction, true);
    document.addEventListener("click", onAttemptedOffTargetInteraction, true);
    return () => {
      document.removeEventListener("pointerdown", onAttemptedOffTargetInteraction, true);
      document.removeEventListener("mousedown", onAttemptedOffTargetInteraction, true);
      document.removeEventListener("click", onAttemptedOffTargetInteraction, true);
    };
  }, [active, isAllowedTourTarget, requestExitGuard]);

  // For `click_target` beats, attach a click listener on the anchored
  // element so the user's natural click (e.g. on a carousel's Next
  // arrow) both (a) performs the element's default action and (b)
  // releases the beat. We don't preventDefault / stopPropagation —
  // the React onClick on the button still fires, advancing the
  // carousel. Poll for the element since cell outputs hydrate async.
  //
  // `releaseOnAnyClick` mode: bypass the inside-anchor check entirely
  // and release on the FIRST click anywhere on the document (except
  // clicks inside the buddy bubble itself, which would accidentally
  // self-advance on bubble button activations). Used for beats whose
  // anchored widget mutates the DOM between pointerdown and click,
  // making the click event's target unreliable (Radix DropdownMenu
  // trigger, sidebar opener). The anchor's onClick still fires
  // normally — we just skip the contains-check that's blocked by
  // the re-targeting.
  useEffect(() => {
    if (!active || !isClickTarget) return;
    if (!releaseOnAnyClick && !actionAnchor) return;
    let cancelled = false;
    let root: HTMLElement | null = null;
    let releaseTimer: number | null = null;
    const activeBeatId = beat?.beatId ?? null;
    const activeWaitUntilGone = beat?.waitUntilGone;
    const release = () => {
      if (cancelled || releaseTimer != null) return;
      releaseTimer = window.setTimeout(() => {
        releaseTimer = null;
        if (!cancelled) {
          if (activeBeatId && activeWaitUntilGone) {
            waitForGoneAndContinue(activeBeatId, activeWaitUntilGone);
          } else {
            handleContinue();
          }
        }
      }, 75);
    };
    const isInside = (target: EventTarget | null) =>
      !!(root && target instanceof Node && root.contains(target));
    const isInsideBuddyBubble = (target: EventTarget | null) => {
      if (!(target instanceof Node)) return false;
      const el = target instanceof Element ? target : target.parentElement;
      return !!el?.closest?.("[data-buddy-bubble='true']");
    };
    const onClick = (e: MouseEvent) => {
      // Skip clicks on the buddy bubble — those are bubble-internal
      // (Next button, exit X) and must not auto-advance.
      if (isInsideBuddyBubble(e.target)) return;
      if (releaseOnAnyClick || isInside(e.target)) {
        if (activeBeatId) {
          hideClickTargetVisuals(activeBeatId);
        }
        release();
      }
    };
    if (releaseOnAnyClick) {
      // No anchor required — listen to every click on the document.
      document.addEventListener("click", onClick, true);
      return () => {
        cancelled = true;
        document.removeEventListener("click", onClick, true);
        if (releaseTimer != null) window.clearTimeout(releaseTimer);
      };
    }
    const attach = (el: HTMLElement) => {
      root = el;
      document.addEventListener("click", onClick, true);
    };
    const tryAttach = () => {
      if (cancelled) return;
      const el = resolveAnchor(actionAnchor);
      if (el) attach(el);
      else window.setTimeout(tryAttach, 100);
    };
    tryAttach();
    return () => {
      cancelled = true;
      document.removeEventListener("click", onClick, true);
      if (releaseTimer != null) window.clearTimeout(releaseTimer);
    };
  }, [
    active,
    isClickTarget,
    actionAnchor,
    releaseOnAnyClick,
    beat?.beatId,
    beat?.waitUntilGone,
    handleContinue,
    hideClickTargetVisuals,
    waitForGoneAndContinue,
  ]);

  // Gesture detection for demo beats. We watch document-level events
  // in capture phase (so Plotly's / React Flow's own preventDefault
  // on the chart canvas can't swallow them) and check whether the
  // target is inside the anchor element.
  //   drag_zoom          → mousedown→mouseup with > DRAG_THRESHOLD
  //                        movement on the anchor counts as a zoom.
  //   double_click_reset → native dblclick on the anchor.
  //   canvas_pan         → same drag detector as drag_zoom; the
  //                        gesture is the same (click-drag-release),
  //                        only the rendered animation differs.
  //   canvas_pinch_zoom  → wheel event with ctrlKey on the anchor.
  //                        Trackpad pinch and Ctrl+scroll both fire
  //                        this in every modern browser; React Flow
  //                        uses the exact same signal internally for
  //                        zoom. A small wheel-delta threshold avoids
  //                        firing on accidental scroll touches.
  // On detection: hide the demo immediately (setGestureCaptured) so
  // the looping animation doesn't fight the user's actual gesture,
  // then advance after a short delay so the resulting zoom/pan/reset
  // registers before the buddy moves on.
  const [gestureCaptured, setGestureCaptured] = useState(false);
  useEffect(() => {
    setGestureCaptured(false);
  }, [beat?.beatId]);
  useEffect(() => {
    if (!active || !gestureAnchor) return;
    if (
      !isDragZoomDemo &&
      !isDoubleClickResetDemo &&
      !isCanvasPanDemo &&
      !isCanvasPinchZoomDemo
    ) {
      return;
    }
    const DRAG_THRESHOLD_PX = 24;
    const PINCH_DELTA_THRESHOLD = 8;
    const ADVANCE_DELAY_MS = 700;
    let cancelled = false;
    let advanceTimer: number | null = null;
    let cleanup: (() => void) | null = null;
    const fire = () => {
      if (cancelled) return;
      setGestureCaptured(true);
      if (advanceTimer != null) return;
      advanceTimer = window.setTimeout(handleContinue, ADVANCE_DELAY_MS);
    };
    const attach = (root: HTMLElement) => {
      let downX = 0;
      let downY = 0;
      let downInside = false;
      const isInsideRoot = (e: MouseEvent | WheelEvent) => {
        if (root.contains(e.target as Node)) return true;
        const r = root.getBoundingClientRect();
        return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      };
      const isDragKind = isDragZoomDemo || isCanvasPanDemo;
      const onDown = (e: MouseEvent) => {
        downInside = isInsideRoot(e);
        if (downInside) {
          downX = e.clientX;
          downY = e.clientY;
        }
      };
      const onUp = (e: MouseEvent) => {
        if (!isDragKind) return;
        if (!downInside) return;
        const dx = e.clientX - downX;
        const dy = e.clientY - downY;
        if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) fire();
      };
      const onDbl = (e: MouseEvent) => {
        if (!isDoubleClickResetDemo) return;
        if (!isInsideRoot(e)) return;
        fire();
      };
      const onWheel = (e: WheelEvent) => {
        if (!isCanvasPinchZoomDemo) return;
        if (!isInsideRoot(e)) return;
        // ctrlKey is true for both trackpad-pinch and Ctrl+scroll;
        // browsers synthesise it for the pinch gesture. We deliberately
        // do NOT fire on plain scroll — React Flow ignores plain
        // scroll on the canvas (uses it for page scroll instead), so
        // firing on it would advance the beat without the user
        // actually zooming.
        if (!e.ctrlKey) return;
        if (Math.abs(e.deltaY) < PINCH_DELTA_THRESHOLD) return;
        fire();
      };
      document.addEventListener("mousedown", onDown, true);
      document.addEventListener("mouseup", onUp, true);
      document.addEventListener("dblclick", onDbl, true);
      document.addEventListener("wheel", onWheel, { capture: true, passive: true });
      cleanup = () => {
        document.removeEventListener("mousedown", onDown, true);
        document.removeEventListener("mouseup", onUp, true);
        document.removeEventListener("dblclick", onDbl, true);
        document.removeEventListener("wheel", onWheel, { capture: true } as any);
      };
    };
    const tryAttach = () => {
      if (cancelled) return;
      const el = resolveAnchor(gestureAnchor);
      if (el) attach(el);
      else window.setTimeout(tryAttach, 100);
    };
    tryAttach();
    return () => {
      cancelled = true;
      cleanup?.();
      if (advanceTimer != null) window.clearTimeout(advanceTimer);
    };
  }, [
    active,
    gestureAnchor,
    isDragZoomDemo,
    isDoubleClickResetDemo,
    isCanvasPanDemo,
    isCanvasPinchZoomDemo,
    handleContinue,
  ]);

  if (!mounted || !beat) return null;

  const title = beat.copy?.title ?? "";
  const body = beat.copy?.body ?? "";
  const cta = beat.copy?.cta || "Next";
  // Phase detection: beats seeded with a beatId starting with "alert_"
  // are the turn-2 alert-mode walkthrough. Used below to swap the
  // bubble's dark-zinc palette for an icy-blue one that matches the
  // AlertCanvas / AlertCellModal surfaces. Mid-tour theming like this
  // cues the viewer that they've crossed into a new mode without
  // needing a separate banner.
  const isAlertPhase = beat.beatId.startsWith("alert_");
  const pinMobileBubbleAboveCell =
    isMobileViewport && !beat.bubblePlacement && spotlightAnchor?.kind === "cell_index";
  const shouldPinFinalSummaryTop =
    shouldLiftFinalSummary && !beat.bubblePlacement;
  const isDesktopHandoffInput =
    !isMobileViewport && beat.beatId === "tour_handoff_input";
  const shouldPinHandoffAboveInput =
    isDesktopHandoffInput && !beat.bubblePlacement;
  const effectiveBubblePlacement = pinMobileBubbleAboveCell
    ? "anchorAbove"
    : shouldPinFinalSummaryTop
      ? "topCenter"
      : shouldPinHandoffAboveInput
        ? "anchorAbove"
    : beat.bubblePlacement ?? "center";
  const effectiveBubbleRect = pinMobileBubbleAboveCell ? rect : bubbleRect;
  const effectiveBubbleOffset =
    beat.bubbleOffset ??
    (isDesktopHandoffInput ? { y: -160 } : undefined);
  const useMissingAnchorFallback =
    Boolean(spotlightAnchor) && !rect && anchorFallbackReady;

  // Compute bubble position once. When the placement depends on an
  // anchor rect that hasn't resolved yet, `computeBubbleStyle` already
  // returns `{...fallback, visibility: "hidden"}`. Treat that as "not
  // ready to render" and skip the bubble JSX entirely — otherwise the
  // hidden fallback element can still flash visibly during the mount/
  // animate-in window between beats, producing the "bubble jumps to
  // center-right for a beat" glitch on transitions.
  const bubbleStyle = computeBubbleStyle(
    useMissingAnchorFallback ? null : effectiveBubbleRect,
    useMissingAnchorFallback ? false : isClickTarget,
    useMissingAnchorFallback ? "center" : effectiveBubblePlacement,
    effectiveBubbleOffset,
  );
  // Also drop the bubble the moment the current beat is dismissed
  // locally — same trigger that unmounts the spotlight ring + green
  // click ring — so all three pieces of overlay chrome disappear in
  // the same frame instead of the bubble lingering until the next
  // beat arrives.
  const bubbleReady =
    bubbleStyle.visibility !== "hidden" &&
    dismissedClickChromeBeatId !== beat.beatId;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] pointer-events-none"
      data-buddy-overlay
      aria-live="polite"
    >
      {/* Dimmed backdrop. Three rendering modes:
            1. `noAnchor` beats render NOTHING here — the bubble
               below is the entire UI (used for the alert tour's
               welcome slide, where blacking out the canvas would
               just be noise the user has to skip past).
            2. `highlightOnly` beats render a ring around the anchor
               but DROP the 78% blackout shadow. The rest of the
               page stays fully visible — soft pointing instead of
               modal demand.
            3. Default (replay beats) gets the classic spotlight
               with the box-shadow dimmer, or a flat semi-
               transparent fallback when no anchor resolves. */}
      {beat.noAnchor || dismissedClickChromeBeatId === beat.beatId ? null : rect ? (
        <div
          // No transition / no pulse on this div by design:
          //   - transition-all interpolated every micro rect change (cell
          //     output hydration shifts height by 1-2px during streaming),
          //     turning small rect updates into visible jelly/shake.
          //   - animate-pulse animated the *element's* opacity, but the
          //     box-shadow dimmer rides on this element — so a pulse
          //     made the entire 78% blackout fade to ~39% twice/second,
          //     which the eye reads as the whole world shaking.
          // Snap precisely to the anchor; the rect updater (rAF + rounded
          // dedupe) already prevents per-pixel spam.
          //
          // In click_target / demo modes the dimmer is non-interactive
          // so the user can interact with the anchored element (carousel
          // arrow, Plotly chart) underneath.
          className={cn(
            "absolute",
            // Two ring styles only:
            //   1. highlightOnly: a SINGLE thin white ring. Same
            //      visual language as the replay tour but slimmed
            //      down — no offset trick (the offset paints a
            //      second darker ring around the first, doubling
            //      the visual weight which read as "chunky double
            //      box"). 1.5px white is just enough to register
            //      without dominating the component.
            //   2. Default (replay beats): unchanged — classic thick
            //      white double-ring spotlight the replay tour was
            //      designed against. Replay copy never asked for a
            //      lighter touch so we leave it alone.
            //
            // For click_target beats we keep the white ring AND the
            // emerald pulsing ring stacked. With the slim 1.5px
            // white the stack doesn't look chunky anymore — the
            // green pulse rides naturally on top as the action cue.
            beat.hideSpotlightRing
              ? "ring-0"
              // Mobile cell beats are visually demoted to the
              // highlightOnly thin-ring look (see boxShadow block
              // below for the matching no-blackout change). User
              // feedback: the 78% blackout on mobile replay cells was
              // "too much" — it made the streaming content above/
              // below the spotlighted cell invisible.
              : beat.highlightOnly || isMobileCellBeat
              ? "ring-[1.5px] ring-white/90"
              : "ring-[3px] ring-white ring-offset-[3px] ring-offset-black/50",
            // highlightOnly = no dim, so let the user keep
            // interacting with the page underneath the ring.
            // Same on mobile cell beats: no blackout, no click-to-skip
            // (user advances via the bubble's Next button).
            beat.highlightOnly || isMobileCellBeat
              ? "pointer-events-none"
              : allowSpotlightClickToSkip
                ? "pointer-events-auto"
                : "pointer-events-none",
          )}
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: spotlightBorderRadius,
            // No box-shadow dim for highlightOnly beats. The user
            // explicitly fed back that the soft blackout was "weird"
            // and asked for it gone — the thin white ring alone is
            // enough of a pointer.
            //
            // Mobile cell beats also drop the blackout: the 78% dim
            // on phones swallowed too much of the streaming content
            // above/below the spotlighted cell, making the rest of
            // the demo unreadable while a beat was active. Desktop
            // replay beats keep the classic 78% blackout.
            boxShadow: beat.highlightOnly || isMobileCellBeat
              ? undefined
              : "0 0 0 9999px rgba(0, 0, 0, 0.78)",
          }}
          onClick={
            // No click-to-skip when there's no dim to click on — both
            // highlightOnly beats and mobile cell beats fall into
            // this branch. Users advance via the bubble's Next button.
            beat.highlightOnly || isMobileCellBeat
              ? undefined
              : allowSpotlightClickToSkip
                ? handleContinue
                : undefined
          }
        />
      ) : (
        // No anchor rect AND not noAnchor → flat dim fallback.
        // highlightOnly with no resolvable anchor falls through to
        // here as well, but we honour highlightOnly by skipping the
        // dim (we'd have nothing useful to highlight, so just let
        // the bubble float).
        beat.highlightOnly || isMobileCellBeat ? null : (
          <div
            className={cn(
              "absolute inset-0 bg-black/78",
              allowSpotlightClickToSkip ? "pointer-events-auto" : "pointer-events-none"
            )}
            onClick={allowSpotlightClickToSkip ? handleContinue : undefined}
          />
        )
      )}

      {/* click_target affordance: pulsing green ring layered over the
          spotlight + a "Click here" pill anchored above. Both are
          pointer-events-none so the underlying button still receives
          the click. The pulse is on a SIBLING element of the dimmer
          (not the dimmer itself) so the 78% blackout shadow doesn't
          flicker — that was the world-shake bug from the older pulse
          implementation. */}
      {isClickTarget && clickRect && dismissedClickChromeBeatId !== beat.beatId ? (
        <>
          <div
            className="absolute rounded-xl pointer-events-none ring-[3px] ring-emerald-400 animate-pulse"
            style={{
              top: clickRect.top,
              left: clickRect.left,
              width: clickRect.width,
              height: clickRect.height,
            }}
          />
          {/* Tooltip-style pill: glassy gradient + click icon + a
              CSS triangle tail that points at the target.
              animate-buddy-click-hint defined in globals.css.

              Placement strategy: prefer LEFT of the click target
              (vertically centered, tail points right). This keeps the
              pill out of the way of any content above the target —
              critical for `tour_handoff_input` where the click target
              is the Send button at the bottom of the viewport: a
              top-anchored pill obscures the prefilled prompt text
              inside the textarea right above it. Falls back to above
              the target only when there isn't room to the left
              (button hugs the left edge of the screen). */}
          {(() => {
            const PILL_W = 80;
            const PILL_H = 24;
            const GAP = 10;
            const vw =
              typeof window !== "undefined" ? window.innerWidth : 9999;
            const vh =
              typeof window !== "undefined" ? window.innerHeight : 9999;
            const forceAbove = beat.clickHintPlacement === "above";
            const placeLeft = !forceAbove && clickRect.left - GAP - PILL_W >= 8;
            const tailRightPlacement = placeLeft;
            const top = placeLeft
              ? Math.max(
                  8,
                  Math.min(
                    vh - PILL_H - 8,
                    clickRect.top + clickRect.height / 2 - PILL_H / 2,
                  ),
                )
              : Math.max(8, clickRect.top - 46);
            const left = placeLeft
              ? clickRect.left - GAP - PILL_W
              : Math.max(
                  8,
                  Math.min(
                    vw - PILL_W - 8,
                    clickRect.left + clickRect.width / 2 - PILL_W / 2,
                  ),
                );
            return (
              <div
                className="absolute pointer-events-none select-none animate-buddy-click-hint"
                style={{ top, left }}
              >
                <div
                  className={cn(
                    "relative flex items-center gap-1.5",
                    "rounded-full px-2.5 py-1",
                    "bg-gradient-to-b from-emerald-300 to-emerald-500",
                    "text-[11.5px] font-semibold tracking-tight text-emerald-950",
                    "shadow-[0_4px_14px_-2px_rgba(16,185,129,0.55)]",
                    "ring-1 ring-emerald-200/60",
                  )}
                >
                  <MousePointerClick
                    className="w-3.5 h-3.5"
                    strokeWidth={2.5}
                  />
                  <span>Click</span>
                  {tailRightPlacement ? (
                    // Right-pointing triangle tail (pill is left of target).
                    <span
                      aria-hidden
                      className="absolute -right-[5px] top-1/2 -translate-y-1/2"
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: "5px solid transparent",
                        borderBottom: "5px solid transparent",
                        borderLeft: "6px solid rgb(16 185 129)",
                      }}
                    />
                  ) : (
                    // Down-pointing triangle tail (pill above target).
                    <span
                      aria-hidden
                      className="absolute left-1/2 -bottom-[5px] -translate-x-1/2"
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: "5px solid transparent",
                        borderRight: "5px solid transparent",
                        borderTop: "6px solid rgb(16 185 129)",
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })()}
        </>
      ) : null}

      {/* Drag-zoom gesture demo. Cursor + dashed selection rectangle
          loop a click-and-drag motion in the lower 60% of the anchor
          rect (charts sit below the cell header). All elements are
          pointer-events-none so they never block the user from
          actually trying the gesture themselves. Animation uses CSS
          keyframes (defined in globals.css) — no JS state, no rAF,
          one composited transform per element. */}
      {isDragZoomDemo && rect && !gestureCaptured ? (
        <div
          className="absolute pointer-events-none overflow-hidden"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        >
          {/* Demo region — inset so the cursor doesn't ride the cell
              edges. Plotly charts typically occupy the lower portion
              of the cell after the header/tabs. */}
          <div
            className="absolute"
            style={{ top: "30%", left: "12%", width: "76%", height: "55%" }}
          >
            <div className="absolute animate-buddy-zoom-rect rounded-md border-2 border-dashed border-emerald-300/90 bg-emerald-300/15 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]" />
            <div className="absolute animate-buddy-zoom-cursor drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
              <MousePointer2
                className="w-5 h-5 text-white"
                strokeWidth={2.25}
                fill="white"
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Double-click-to-reset gesture demo. Stationary cursor in the
          chart center that ticks twice in quick succession + two
          expanding ripple rings to convey "double click". Same pill
          treatment as the drag-zoom demo for visual consistency. */}
      {isDoubleClickResetDemo && rect && !gestureCaptured ? (
        <div
          className="absolute pointer-events-none overflow-hidden"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        >
          {/* Cursor + ripple rings centered on the chart area. */}
          <div
            className="absolute"
            style={{ top: "55%", left: "50%", transform: "translate(-50%, -50%)" }}
          >
            <div className="relative">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full ring-2 ring-emerald-300 animate-buddy-double-ripple-a" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full ring-2 ring-emerald-300 animate-buddy-double-ripple-b" />
              <div className="relative animate-buddy-double-click drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
                <MousePointer2
                  className="w-5 h-5 text-white"
                  strokeWidth={2.25}
                  fill="white"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Canvas-pan gesture demo. Cursor traces a semi-circular ARC
          across the alert canvas with NO trail line. Two icons stack
          and cross-fade at the press/release boundaries so the user
          sees the cursor visibly TRANSITION from a default pointer
          (idle) to a hand (during drag) and back — same OS visual
          language for grabbing a draggable surface. Pointer-events-
          none so the user can drag the canvas underneath at any
          time. Animation is pure CSS keyframes in globals.css. */}
      {isCanvasPanDemo && rect && !gestureCaptured ? (
        <div
          className="absolute pointer-events-none overflow-hidden"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        >
          {/* Demo region — slightly inset so the cursor's arc apex
              (top: 32% of region = ~22% of canvas) doesn't overlap
              the canvas's top toolbar / MiniMap. */}
          <div
            className="absolute"
            style={{ top: "10%", left: "0%", width: "100%", height: "80%" }}
          >
            {/* Single positioning element drives the curved path; two
                stacked icons inside cross-fade between arrow + hand
                so the user reads "pointer turns into grab during the
                drag". The icons share the same anchor point so the
                swap is registered as a state change, not a teleport. */}
            <div className="absolute animate-buddy-pan-cursor drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
              <div className="relative w-5 h-5">
                <MousePointer2
                  className="absolute inset-0 w-5 h-5 text-white animate-buddy-pan-arrow-fade"
                  strokeWidth={2.25}
                  fill="white"
                />
                <Hand
                  className="absolute inset-0 w-5 h-5 text-white animate-buddy-pan-hand-fade"
                  strokeWidth={2.25}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Canvas pinch-to-zoom-OUT gesture demo. Two contact circles
          (no pointer arrows — circles read as "finger touchpoints"
          without the directional ambiguity an arrow brings) start at
          opposite corners of the canvas and CONVERGE toward the
          center, miming the pinch-IN gesture that maps to zoom-OUT.
          No connecting line — the v1 line read as a drawn region
          which is exactly the wrong association for a zoom gesture.
          All elements pointer-events-none so the actual pinch /
          Ctrl+scroll gesture passes through to React Flow. */}
      {isCanvasPinchZoomDemo && rect && !gestureCaptured ? (
        <div
          className="absolute pointer-events-none overflow-hidden"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        >
          <div
            className="absolute"
            style={{ top: "5%", left: "5%", width: "90%", height: "90%" }}
          >
            {/* Top-left contact point. White-filled circle with an
                emerald glow ring — the same green-on-dim language
                used by other gesture demos for visual consistency.
                w-4 h-4 keeps it readable without dominating the
                canvas at typical zoom. */}
            <div className="absolute animate-buddy-pinch-circle-tl">
              <div className="w-4 h-4 rounded-full bg-white ring-2 ring-emerald-300/80 shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
            </div>
            {/* Bottom-right contact point — mirror styling, mirror
                converging keyframes. */}
            <div className="absolute animate-buddy-pinch-circle-br">
              <div className="w-4 h-4 rounded-full bg-white ring-2 ring-emerald-300/80 shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
            </div>
          </div>
        </div>
      ) : null}

      {isModalScrollDemo && rect ? (
        <>
          <style>{`
            @keyframes buddy-scroll-swipe {
              0%   { transform: translate(-50%, 56px); opacity: 0; }
              18%  { opacity: 1; }
              82%  { opacity: 1; }
              100% { transform: translate(-50%, -56px); opacity: 0; }
            }
          `}</style>
          <div
            className="absolute pointer-events-none"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          >
            {/* Track + dot column on the right edge. `right: 28`
                keeps it clear of overlay scrollbars (~16px on Mac)
                and the modal card's rounded corner. */}
            <div
              className="absolute flex flex-col items-center"
              style={{ right: 28, top: "50%", transform: "translateY(-50%)" }}
            >
              {/* Vertical track. Fixed 130px so the dot animation
                  reads as a deliberate gesture (too short looks
                  twitchy, too long looks slow). */}
              <div className="relative w-[3px] h-[130px] rounded-full bg-white/15">
                {/* Animated touch indicator. The keyframe moves it
                    from +56px (below center) to -56px (above center)
                    so the visible travel is ~110px, slightly less
                    than the track height to keep it framed. */}
                <div
                  className="absolute left-1/2 top-1/2 w-6 h-6 rounded-full bg-white/95 ring-2 ring-sky-300 shadow-[0_0_18px_rgba(125,211,252,0.85)]"
                  style={{
                    animation: "buddy-scroll-swipe 1.6s ease-in-out infinite",
                  }}
                />
              </div>
              <span className="mt-2.5 text-[10px] uppercase tracking-widest font-semibold text-sky-100/90 bg-sky-500/20 border border-sky-300/40 rounded px-1.5 py-0.5 backdrop-blur-sm">
                Swipe to scroll
              </span>
            </div>
          </div>
        </>
      ) : null}

      {/* Floating bubble. The bubble DOES capture pointer events so
          its buttons are clickable; the dimmer is either
          pointer-events-none (default) or pointer-events-auto (skip).
          In click_target mode we position the bubble next to the
          spotlight rect (rather than pinned to the right edge) so the
          viewer can visually connect the prompt to the target they
          need to click. pointer-events-auto so clicks inside don't
          bubble to the dimmer.
          Gated on `bubbleReady` (computeBubbleStyle did NOT return
          visibility:hidden) so we never paint the bubble at its
          rect-null fallback position — that fallback is what caused
          the "bubble jumps to center-right" transition glitch. */}
      {bubbleReady ? (
      <div
        className={cn(
          "fixed pointer-events-auto select-none",
          "rounded-2xl border backdrop-blur",
          // px-6 py-5 (was px-5 py-4) gives the copy a touch more
          // breathing room on desktop. On mobile we tighten to
          // px-4 py-3.5 so the bubble takes less vertical real
          // estate (the user explicitly asked for smaller fonts
          // and a more compact bubble on phones).
          "px-4 py-3.5 md:px-6 md:py-5 shadow-2xl",
          // Phase-aware theming. Alert-mode beats (beatId starts with
          // "alert_") render in an icy-blue surface to match the
          // AlertCanvas / AlertCellModal palette and visually announce
          // "you're in alert mode now". Research-mode beats now render
          // in INVERTED colours — light bg + dark text — so the bubble
          // reads as a "guide note" rather than yet another dark modal
          // floating over a dark app. User explicitly asked for this
          // inversion outside of alert pages.
          isAlertPhase
            ? "border-sky-300/25 bg-slate-900/85 text-sky-50"
            : "border-zinc-300/60 bg-white/95 text-zinc-900",
          isClickTarget
            ? "animate-in fade-in zoom-in-95 duration-200"
            : "animate-in fade-in slide-in-from-right-4 duration-200"
        )}
        style={bubbleStyle}
        onClick={(e) => e.stopPropagation()}
        // Prevent Radix dismissable-layer (used by Dialog/DropdownMenu)
        // from interpreting clicks INSIDE the bubble as "outside the
        // dialog" and auto-closing the underlying modal. Radix's
        // pointerdown-outside handler bails out when the event has
        // `defaultPrevented=true`, so calling preventDefault here is
        // the canonical fix. We do NOT stop propagation — the click
        // event still needs to reach the Next/Exit buttons inside the
        // bubble, and preventDefault on pointerdown does NOT block the
        // subsequent click event from firing on the buttons.
        //
        // Symptom this fixes: when a beat's anchor is inside an open
        // dialog (e.g. the schedule presets / nl-input beats), clicking
        // Next would close the dialog underneath, breaking the next
        // beat's anchor resolution and leaving the tour flailing.
        onPointerDown={(e) => e.preventDefault()}
        // Marker attribute used by surrounding Radix dialogs / dropdowns
        // (IcyDialogShell, AlertTopBar's DropdownMenuContent) to detect
        // when an outside-click should be IGNORED — i.e. the click
        // landed on the buddy bubble, not on the page underneath. Each
        // dismissable layer's onInteractOutside checks
        // `target.closest('[data-buddy-bubble]')` and calls preventDefault
        // on the synthetic event, keeping the modal/menu open so the
        // tour can continue stepping through anchors inside it.
        data-buddy-bubble="true"
        role="dialog"
        aria-modal="true"
        aria-labelledby="buddy-title"
      >
        {title ? (
          <div
            id="buddy-title"
            className={cn(
              // Mobile: 13px / md+: 15px. User explicitly asked for
              // smaller internal fonts in the buddy overlay on phones.
              "text-[13px] md:text-[15px] font-semibold tracking-tight leading-tight",
              isAlertPhase ? "text-sky-100" : "text-zinc-900",
            )}
          >
            {title}
          </div>
        ) : null}
        {body ? (
          <div
            className={cn(
              // Mobile: 12px body / md+: 13.5px. Same "smaller fonts
              // on phones" pass.
              "mt-2 md:mt-3 text-[12px] md:text-[13.5px] leading-[1.55] md:leading-[1.6]",
              isAlertPhase ? "text-sky-100/80" : "text-zinc-700",
            )}
          >
            {renderBuddyBody(body, isAlertPhase)}
          </div>
        ) : null}
        <div className="mt-3 md:mt-5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleExit}
            className={cn(
              "text-[11px] md:text-[12.5px] transition-colors",
              isAlertPhase
                ? "text-sky-200/60 hover:text-red-300"
                : "text-zinc-500 hover:text-red-500",
            )}
          >
            Exit tour
          </button>
          {/* Always show a Next button so the viewer has a consistent
              forward affordance on every beat. For click_target beats
              this acts as a manual skip — the user can still click the
              highlighted anchor for the "natural" advance, but the
              Next button is always available as a bypass. Keeps the
              UX from feeling like it's gating them behind an
              interaction they might not realize is required. */}
          <button
            type="button"
            onClick={handlePrimaryAction}
            className={cn(
              "rounded-full px-3.5 py-1.5 md:px-4 text-[11.5px] md:text-[13px] font-semibold transition",
              "active:scale-[0.98]",
              isAlertPhase
                ? "bg-sky-100 text-slate-900 hover:bg-white"
                : "bg-zinc-900 text-white hover:bg-zinc-800",
            )}
            autoFocus
          >
            {cta}
          </button>
        </div>
      </div>
      ) : null}
      {showExitGuard ? (
        <div
          className="fixed inset-0 z-[210] pointer-events-auto flex items-center justify-center bg-black/70 px-4"
          data-buddy-exit-confirm="true"
          role="dialog"
          aria-modal="true"
          aria-labelledby="buddy-exit-confirm-title"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/15 bg-zinc-950 px-6 py-5 text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              id="buddy-exit-confirm-title"
              className="text-lg font-semibold tracking-tight"
            >
              Exit the tour?
            </div>
            <div className="mt-4 space-y-3 text-sm leading-6 text-zinc-300">
              <p>
                You are in the middle of learning how to use{" "}
                <strong className="font-semibold text-white">Xynth properly</strong>.
              </p>
              <p>
                Leaving now means missing the parts that help you{" "}
                <strong className="font-semibold text-white">find signals</strong>,{" "}
                <strong className="font-semibold text-white">understand alerts</strong>,
                and{" "}
                <strong className="font-semibold text-white">extract the full potential</strong>.
              </p>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleConfirmExit}
                className="rounded-full px-4 py-2 text-sm font-medium text-zinc-400 transition hover:text-red-300"
              >
                Exit anyway
              </button>
              <button
                type="button"
                onClick={handleStayInTour}
                className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90"
                autoFocus
              >
                Stay in tour
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
}

// Render a buddy body string with lightweight markdown support:
//   • `**text**`  → bold
//   • `\n\n`      → paragraph break
// Intentionally NOT a full markdown parser — keeping the schema a
// plain string means backend / seed scripts don't need to build
// React nodes, and the scope is just "terse pitch copy". Escape
// hatch for raw text is: don't include the tokens.
//
// `isAlertPhase` threads the bubble's phase-aware theming down into
// the bold / standalone-bold accents so research-mode (light bg)
// bubbles get dark accents instead of the sky-50 / white text that
// would be invisible on the inverted background.
function renderBuddyBody(body: string, isAlertPhase: boolean): ReactNode {
  const paragraphs = body.split(/\n{2,}/);
  return paragraphs.map((para, i) => {
    const lines = para.split("\n").map((line) => line.trim()).filter(Boolean);
    const isList = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
    const isStandaloneBold = /^\*\*[^*]+\*\*$/.test(para.trim());
    if (isList) {
      return (
        <ul key={i} className={cn("space-y-1.5 pl-4", i > 0 ? "mt-3" : undefined)}>
          {lines.map((line, j) => (
            <li
              key={j}
              className={cn(
                "list-disc pl-1",
                isAlertPhase ? "marker:text-sky-300/70" : "marker:text-zinc-400",
              )}
            >
              {renderInlineBold(line.replace(/^[-*]\s+/, ""), isAlertPhase)}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        className={cn(
          i > 0 ? "mt-3" : undefined,
          isStandaloneBold
            ? cn(
                "text-[13px] font-semibold",
                isAlertPhase ? "text-sky-50" : "text-zinc-900",
              )
            : undefined,
        )}
      >
        {renderInlineBold(para, isAlertPhase)}
      </p>
    );
  });
}

function renderInlineBold(text: string, isAlertPhase: boolean): ReactNode[] {
  // Split on `**...**` keeping the capture. Odd-indexed chunks are
  // bold; even-indexed are plain. Double-asterisk is deliberately
  // required (not single) so regular mid-word asterisks in tickers
  // / math don't accidentally trigger bold.
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong
        key={i}
        className={cn(
          "font-semibold",
          isAlertPhase ? "text-white" : "text-zinc-900",
        )}
      >
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// Bubble placement. Default (non click_target): fixed right edge,
// vertically centered — leaves the spotlighted cell visually open.
// click_target mode: adjacent to the spotlight rect (right side if
// the viewport can accommodate; otherwise below; otherwise fall back
// to fixed-right). Viewport edges clamped so the bubble is never
// offscreen. Recomputed on every render — cheap since `rect` updates
// are already rAF-coalesced upstream.
function computeBubbleStyle(
  rect: Rect | null,
  isClickTarget: boolean,
  bubblePlacement: "center" | "bottomRight" | "topCenter" | "anchor" | "anchorAbove" | "anchorBelow" | "anchorTopRight" | "anchorLeft" | "modalSidebar" | "modalRight" | "outsideModalRight" = "center",
  bubbleOffset?: { x?: number; y?: number },
): CSSProperties {
  const applyOffset = (style: CSSProperties): CSSProperties => {
    const x = bubbleOffset?.x ?? 0;
    const y = bubbleOffset?.y ?? 0;
    if (!x && !y) return style;
    const next: CSSProperties = { ...style };
    if (typeof next.left === "number") next.left += x;
    if (typeof next.top === "number") next.top += y;
    if (typeof next.right === "number") next.right -= x;
    if (typeof next.bottom === "number") next.bottom -= y;
    return next;
  };
  if (bubblePlacement === "outsideModalRight") {
    const fallback: CSSProperties = {
      right: BUBBLE_RIGHT_OFFSET,
      top: 112,
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
      visibility: "hidden",
    };
    if (!rect || typeof window === "undefined") return fallback;
    const vw = window.innerWidth;
    const GAP = 16;
    const MARGIN = 16;
    const rightSideLeft = rect.left + rect.width + GAP;
    const leftSideLeft = rect.left - BUBBLE_WIDTH - GAP;
    const hasRoomRight = rightSideLeft + BUBBLE_WIDTH <= vw - MARGIN;
    const left = hasRoomRight ? rightSideLeft : Math.max(MARGIN, leftSideLeft);
    return applyOffset({ left, top: rect.top + 72, width: BUBBLE_WIDTH });
  }
  if (bubblePlacement === "anchorLeft") {
    const fallback: CSSProperties = {
      right: BUBBLE_RIGHT_OFFSET,
      top: 96,
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
      visibility: "hidden",
    };
    if (!rect || typeof window === "undefined") return fallback;
    const vh = window.innerHeight;
    const GAP = 16;
    const MARGIN = 16;
    const ESTIMATED_HEIGHT = 180;
    const left = Math.max(MARGIN, rect.left - BUBBLE_WIDTH - GAP);
    const top = Math.max(
      MARGIN,
      Math.min(vh - ESTIMATED_HEIGHT - MARGIN, rect.top - 24),
    );
    return applyOffset({ left, top, width: BUBBLE_WIDTH });
  }
  if (bubblePlacement === "modalRight") {
    const fallback: CSSProperties = {
      right: 40,
      top: 112,
      width: 292,
      maxWidth: "calc(100vw - 32px)",
      visibility: "hidden",
    };
    if (!rect || typeof window === "undefined") return fallback;
    const WIDTH = 292;
    const left = rect.left + rect.width - WIDTH - 32;
    const top = rect.top + 72;
    return { left, top, width: WIDTH };
  }
  if (bubblePlacement === "modalSidebar") {
    const fallback: CSSProperties = {
      left: 24,
      top: 96,
      width: 292,
      maxWidth: "calc(100vw - 32px)",
      visibility: "hidden",
    };
    if (!rect || typeof window === "undefined") return fallback;
    const WIDTH = 292;
    const left = rect.left + 96;
    const top = rect.top + 72;
    return { left, top, width: WIDTH };
  }
  if (bubblePlacement === "anchorTopRight") {
    const fallback: CSSProperties = {
      right: BUBBLE_RIGHT_OFFSET,
      bottom: 168,
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
      visibility: "hidden",
    };
    if (!rect || typeof window === "undefined") return fallback;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 32;
    const MARGIN = 16;
    const ESTIMATED_HEIGHT = 200;
    const rawLeft = rect.left + rect.width - BUBBLE_WIDTH;
    const left = Math.max(MARGIN, Math.min(vw - BUBBLE_WIDTH - MARGIN, rawLeft));
    const top = Math.max(
      MARGIN,
      Math.min(vh - ESTIMATED_HEIGHT - MARGIN, rect.top - ESTIMATED_HEIGHT - GAP),
    );
    return {
      left,
      top,
      width: BUBBLE_WIDTH,
    };
  }
  // anchorBelow — pin the bubble directly under the anchor rect.
  // Critical for click_target beats whose anchor sits in the TOP-right
  // corner (history icon, three-dot menu trigger). Default click_target
  // adjacency picks LEFT for those (since RIGHT has no room), but with
  // a near-top anchor the LEFT branch clamps the bubble against the
  // top-left and visually crowds the highlighted icon. anchorBelow
  // keeps the bubble in proximity to the target — right under it,
  // horizontally centered on the rect — so the green-ringed click
  // target stays clearly visible above. While the rect resolves we
  // hide the bubble so it doesn't flash in the centered fallback
  // position first (same trick as the click_target adjacency below).
  if (bubblePlacement === "anchorBelow") {
    if (!rect) {
      return {
        right: BUBBLE_RIGHT_OFFSET,
        top: "50%",
        transform: "translateY(-50%)",
        width: BUBBLE_WIDTH,
        maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
        visibility: "hidden",
      };
    }
    if (typeof window !== "undefined") {
      const vw = window.innerWidth;
      const GAP = 12;
      const MARGIN = 16;
      const rawLeft = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
      const left = Math.max(MARGIN, Math.min(vw - BUBBLE_WIDTH - MARGIN, rawLeft));
      return applyOffset({ left, top: rect.top + rect.height + GAP, width: BUBBLE_WIDTH });
    }
    return {
      right: BUBBLE_RIGHT_OFFSET,
      top: "50%",
      transform: "translateY(-50%)",
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
    };
  }
  if (bubblePlacement === "anchorAbove") {
    if (!rect) {
      return {
        right: BUBBLE_RIGHT_OFFSET,
        top: "50%",
        transform: "translateY(-50%)",
        width: BUBBLE_WIDTH,
        maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
        visibility: "hidden",
      };
    }
    if (typeof window !== "undefined") {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const GAP = 10;
      const MARGIN = 16;
      const rawLeft = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
      const left = Math.max(MARGIN, Math.min(vw - BUBBLE_WIDTH - MARGIN, rawLeft));
      // MOBILE-ONLY auto-flip. On desktop the anchorAbove geometry
      // has worked fine for a year — never touch it. On mobile the
      // blog-article tour was tripping on a specific failure mode:
      // beats with `scrollOffsetY: 72` land `rect.top` at ~72px from
      // the top of the viewport, leaving only ~46px of usable space
      // above — far less than a 140-px-tall mobile bubble. The
      // previous floor `Math.max(120, ...)` on `maxHeight` silently
      // let the bubble overflow ABOVE the viewport (top edge at
      // y=-58), making it invisible. User feedback: "the buddy
      // overlay are going too far up and not being visible... on
      // desktop it works fine, this is on mobile."
      //
      // Mobile flip ladder:
      //   1. enough room above → use the original anchorAbove geom
      //      (no behaviour change — same as desktop).
      //   2. not enough above, enough below → fall through to
      //      anchorBelow geometry.
      //   3. neither fits → pin to top of viewport. Bubble overlaps
      //      the anchor's top edge but stays VISIBLE.
      const isMobile = vw <= 767;
      const MIN_BUBBLE_HEIGHT = 140;
      if (isMobile) {
        const spaceAbove = rect.top - MARGIN - GAP;
        const spaceBelow = vh - (rect.top + rect.height) - MARGIN - GAP;
        if (spaceAbove < MIN_BUBBLE_HEIGHT) {
          if (spaceBelow >= MIN_BUBBLE_HEIGHT) {
            const top = rect.top + rect.height + GAP;
            return applyOffset({
              left,
              top,
              width: BUBBLE_WIDTH,
              maxHeight: Math.max(MIN_BUBBLE_HEIGHT, vh - top - MARGIN),
            });
          }
          return applyOffset({
            left,
            top: MARGIN,
            width: BUBBLE_WIDTH,
            maxHeight: Math.min(
              vh - 2 * MARGIN,
              Math.max(MIN_BUBBLE_HEIGHT, vh * 0.5),
            ),
          });
        }
      }
      // Desktop path (and mobile-with-enough-space-above) — original
      // anchorAbove geometry, untouched. Pin the bubble's BOTTOM
      // edge `GAP` pixels above the anchor's top edge; CSS uses the
      // bubble's natural content height to figure out where the top
      // lands. `maxHeight` clamps the bubble so it can't overflow
      // past the top of the viewport when copy is unusually long.
      const bottomFromBottom = Math.max(MARGIN, vh - rect.top + GAP);
      const maxHeightPx = Math.max(120, vh - bottomFromBottom - MARGIN);
      return applyOffset({
        left,
        bottom: bottomFromBottom,
        width: BUBBLE_WIDTH,
        maxHeight: maxHeightPx,
      });
    }
    return {
      right: BUBBLE_RIGHT_OFFSET,
      top: "50%",
      transform: "translateY(-50%)",
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
    };
  }
  // Bottom-right placement — used by:
  //   1. the alert-tour welcome beat (no anchor; the bubble greets
  //      from the corner instead of dominating the middle of the
  //      screen, which the earlier centered placement read as
  //      "another modal to dismiss"); and
  //   2. click_target beats whose anchor sits in the TOP-right corner
  //      (e.g. the alert top-bar history icon). The default click_target
  //      adjacency picks "left of the target", which on a near-top
  //      anchor clamps the bubble against the top-left and visually
  //      crowds the green-ringed click target. Pinning to the bottom-
  //      right instead frees the entire top region so the highlighted
  //      icon stays clearly visible and clickable.
  // We intentionally honour this placement EVEN when a rect is present
  // — the spotlight ring is what tells the user where to click, the
  // bubble's job is just to carry the copy, so it doesn't need to be
  // adjacent to the target.
  if (bubblePlacement === "bottomRight") {
    return applyOffset({
      right: BUBBLE_RIGHT_OFFSET,
      bottom: BUBBLE_RIGHT_OFFSET,
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
    });
  }
  // Top-center placement — used by beats whose spotlight is INSIDE a
  // viewport-filling modal (history explore, dashboard explore, etc).
  // The default right-edge fallback puts the bubble vertically
  // centered, which lands smack in the middle of the modal's right
  // pane (the rendered dashboard) and obscures it. Pinning to the
  // top of the viewport keeps the bubble above the modal's main
  // content while still being visible against the modal's dim
  // backdrop. No rect dependency — we ignore the anchor rect for
  // positioning so the bubble doesn't shift as the modal animates in.
  if (bubblePlacement === "topCenter") {
    if (typeof window !== "undefined") {
      const vw = window.innerWidth;
      const MARGIN = 16;
      const x = bubbleOffset?.x ?? 0;
      const y = bubbleOffset?.y ?? 0;
      const width = Math.min(BUBBLE_WIDTH, vw - MARGIN * 2);
      const centeredLeft = vw / 2 - width / 2;
      const left = Math.max(MARGIN, Math.min(vw - width - MARGIN, centeredLeft + x));
      return {
        top: Math.max(MARGIN, BUBBLE_RIGHT_OFFSET + y),
        left,
        width,
        maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
      };
    }
    return {
      top: BUBBLE_RIGHT_OFFSET,
      left: "50%",
      transform: "translateX(-50%)",
      width: BUBBLE_WIDTH,
      maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
    };
  }
  const fallback: CSSProperties = {
    right: BUBBLE_RIGHT_OFFSET,
    top: "50%",
    transform: "translateY(-50%)",
    width: BUBBLE_WIDTH,
    maxWidth: `calc(100vw - ${BUBBLE_RIGHT_OFFSET * 2}px)`,
  };
  // Should we position the bubble adjacent to the anchor rect? True for
  // click_target beats (need to draw the eye to the click target) AND
  // for any beat with explicit `bubblePlacement: "anchor"` (used to pin
  // the bubble next to a non-clickable highlight, e.g. the email chip
  // / run-now / pause / open-dropdown beats where the default right-
  // edge fallback put the bubble far away from the target the copy was
  // referring to).
  const wantsAdjacent = isClickTarget || bubblePlacement === "anchor";
  // Hide the bubble entirely while we wait for the rect to be measured.
  // Without this guard the first paint uses the centered `fallback`
  // (top:50%), then jumps to the resolved position once the anchor
  // poll completes ~100-300ms later — visually a "flash at the top,
  // then move down" glitch the viewer always notices on slower mounts.
  // Returning `visibility: hidden` keeps the layout stable so the
  // slide-in animation still plays cleanly when the rect arrives.
  if (wantsAdjacent && !rect) {
    return { ...fallback, visibility: "hidden" };
  }
  if (!wantsAdjacent || !rect) return fallback;
  if (typeof window === "undefined") return fallback;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const GAP = 12;
  const MARGIN = 16;
  // Bumped from 180 to cover the longest current copy ("tour_handoff_input"
  // is title + 4-line body + Exit tour ≈ 240-260px). Under-estimating
  // pushes the clamp the wrong way and lets the bubble's bottom escape
  // the viewport, which manifests as the "Got it" / "Exit tour" row
  // being cropped off below the docked input bar.
  const ESTIMATED_HEIGHT = 280;
  const rightEdge = rect.left + rect.width;
  const bottomEdge = rect.top + rect.height;
  // Prefer right of the target.
  if (rightEdge + GAP + BUBBLE_WIDTH + MARGIN <= vw) {
    const rawTop = rect.top + rect.height / 2 - ESTIMATED_HEIGHT / 2;
    const top = Math.max(MARGIN, Math.min(vh - ESTIMATED_HEIGHT - MARGIN, rawTop));
    return { left: rightEdge + GAP, top, width: BUBBLE_WIDTH };
  }
  // Otherwise LEFT of the target. Critical for anchors at the right
  // edge of the viewport (top-bar action cluster: email chip, Run
  // Now, Pause, the open 3-dot dropdown). Without this, anchors at
  // the right edge would skip "right" (no space), then try "below"
  // (often misaligned because the chip is near the top of the
  // viewport), and the bubble would land far from the actual target.
  if (rect.left - GAP - BUBBLE_WIDTH - MARGIN >= 0) {
    const rawTop = rect.top + rect.height / 2 - ESTIMATED_HEIGHT / 2;
    const top = Math.max(MARGIN, Math.min(vh - ESTIMATED_HEIGHT - MARGIN, rawTop));
    return { left: rect.left - GAP - BUBBLE_WIDTH, top, width: BUBBLE_WIDTH };
  }
  // Otherwise below.
  if (bottomEdge + GAP + ESTIMATED_HEIGHT + MARGIN <= vh) {
    const rawLeft = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
    const left = Math.max(MARGIN, Math.min(vw - BUBBLE_WIDTH - MARGIN, rawLeft));
    return { left, top: bottomEdge + GAP, width: BUBBLE_WIDTH };
  }
  // Otherwise above. Critical for click_targets pinned to the docked
  // input bar (`tour_handoff_input`): the target sits at the very
  // bottom of the viewport, "below" never fits, "right" rarely fits at
  // narrower widths, and the previous code fell through to the
  // centered fallback — which puts the bubble visually disconnected
  // from the Send button. Placing it directly above keeps the
  // prompt→target line of sight intact.
  if (rect.top - GAP - ESTIMATED_HEIGHT - MARGIN >= 0) {
    const rawLeft = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
    const left = Math.max(MARGIN, Math.min(vw - BUBBLE_WIDTH - MARGIN, rawLeft));
    return { left, top: rect.top - GAP - ESTIMATED_HEIGHT, width: BUBBLE_WIDTH };
  }
  return fallback;
}

export default BuddyOverlay;
